import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Play, Pause, Square } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { createAudioContext, playBeepsOnce } from "../lib/alarmAudio";

/**
 * TIME Watch → Utility → Cronometro
 *
 * Cronometro classico con funzioni:
 *  - Avvia
 *  - Pausa
 *  - Riprendi
 *  - Azzera
 *
 * Segnale sonoro: 3 beep singoli quando il cronometro viene arrestato/azzerato
 * (chiusura). Non emette suoni continui né alla partenza.
 *
 * Il tempo è calcolato in modo drift-free: quando è in corsa memorizziamo lo
 * `startedAt` (timestamp reale) e sommiamo l'`accumulatedMs` accumulato nelle
 * pause precedenti. Questo garantisce precisione anche se il tab viene messo
 * in background e i tick vengono throttlati dal browser.
 */

const pad2 = (n) => String(n).padStart(2, "0");
const pad3 = (n) => String(n).padStart(3, "0");

export default function TimeWatchCronometroPage() {
  // status: 'idle' | 'running' | 'paused'
  const [status, setStatus] = useState("idle");
  const [displayMs, setDisplayMs] = useState(0);

  const startedAtRef = useRef(null); // ms epoch quando è partito il tratto attuale
  const accumulatedRef = useRef(0); // ms totali accumulati prima del tratto attuale
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);

  const ensureAudio = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = createAudioContext();
    }
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      audioCtxRef.current.resume().catch(() => {});
    }
    return audioCtxRef.current;
  };

  // Loop di refresh (requestAnimationFrame) mentre è in corsa.
  // Se il tab è in background rAF si ferma, ma appena torna visibile
  // ricalcoliamo dal timestamp reale, quindi la precisione è mantenuta.
  useEffect(() => {
    if (status !== "running") return;

    const tick = () => {
      const now = Date.now();
      const elapsed = accumulatedRef.current + (now - startedAtRef.current);
      setDisplayMs(elapsed);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    // Fallback: ricalcolo periodico anche in background (setInterval continua
    // a girare, anche se throttlato). Assicura un aggiornamento almeno ogni 500ms.
    const iv = setInterval(() => {
      if (startedAtRef.current == null) return;
      const now = Date.now();
      const elapsed = accumulatedRef.current + (now - startedAtRef.current);
      setDisplayMs(elapsed);
    }, 500);

    const onVis = () => {
      if (document.visibilityState === "visible" && startedAtRef.current != null) {
        const now = Date.now();
        setDisplayMs(accumulatedRef.current + (now - startedAtRef.current));
      }
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [status]);

  const handleStart = () => {
    if (status === "running") return;
    ensureAudio(); // sblocca l'audio (gesto utente) per il beep di stop successivo
    startedAtRef.current = Date.now();
    setStatus("running");
  };

  const handlePause = () => {
    if (status !== "running") return;
    const now = Date.now();
    accumulatedRef.current += now - startedAtRef.current;
    startedAtRef.current = null;
    setDisplayMs(accumulatedRef.current);
    setStatus("paused");
    // Beep singolo di conferma? Il requisito è "3 beep all'arresto/chiusura"
    // → l'arresto avviene con Azzera. La pausa non è considerata chiusura.
  };

  const handleResume = () => {
    if (status !== "paused") return;
    ensureAudio();
    startedAtRef.current = Date.now();
    setStatus("running");
  };

  const handleReset = () => {
    // Se il cronometro è stato usato (in corsa o in pausa con tempo > 0),
    // "chiudiamo" la sessione: 3 beep + azzeramento.
    const wasActive = status !== "idle";
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    startedAtRef.current = null;
    accumulatedRef.current = 0;
    setDisplayMs(0);
    setStatus("idle");
    if (wasActive) {
      const ctx = ensureAudio();
      if (ctx) playBeepsOnce(ctx, 3);
    }
  };

  // Scomposizione del tempo per il display.
  const totalMs = Math.max(0, displayMs);
  const hh = Math.floor(totalMs / 3600000);
  const mm = Math.floor((totalMs % 3600000) / 60000);
  const ss = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-3xl mx-auto">
      <Link
        to="/innovazione/time-watch/utility"
        data-testid="time-watch-cronometro-back-link"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Utility
      </Link>

      <PageHeader
        eyebrow="Utility"
        title="Cronometro"
        description="Avvia, metti in pausa e azzera. Precisione al millisecondo."
        testId="time-watch-cronometro-header"
      />

      {/* Display */}
      <div
        data-testid="cronometro-display"
        className={`glass-card p-8 sm:p-12 mb-8 text-center transition-colors ${
          status === "running"
            ? "border-emerald-400/30"
            : ""
        }`}
      >
        <div
          data-testid="cronometro-time"
          className="font-extralight text-white tracking-tight tabular-nums leading-none flex items-baseline justify-center"
        >
          <span className="text-6xl sm:text-7xl lg:text-8xl">
            {pad2(hh)}
            <span className="text-white/30 mx-1">:</span>
            {pad2(mm)}
            <span className="text-white/30 mx-1">:</span>
            {pad2(ss)}
          </span>
          <span
            data-testid="cronometro-ms"
            className="text-2xl sm:text-3xl lg:text-4xl text-white/50 ml-2 sm:ml-3"
          >
            .{pad3(ms)}
          </span>
        </div>
        {status === "paused" && (
          <div
            data-testid="cronometro-paused-notice"
            className="mt-6 text-[11px] tracking-[0.32em] uppercase text-white/40"
          >
            In pausa
          </div>
        )}
        {status === "running" && (
          <div
            data-testid="cronometro-running-notice"
            className="mt-6 text-[11px] tracking-[0.32em] uppercase text-emerald-300/80"
          >
            In corso
          </div>
        )}
      </div>

      {/* Controls */}
      <div
        data-testid="cronometro-controls"
        className="flex items-center justify-center gap-4 flex-wrap"
      >
        {status === "idle" && (
          <button
            type="button"
            onClick={handleStart}
            data-testid="cronometro-start-button"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500/90 text-black text-sm tracking-wide font-medium hover:bg-emerald-400 transition"
          >
            <Play className="w-4 h-4" />
            Avvia
          </button>
        )}
        {status === "running" && (
          <button
            type="button"
            onClick={handlePause}
            data-testid="cronometro-pause-button"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/15 text-white text-sm tracking-wide hover:bg-white/15 transition"
          >
            <Pause className="w-4 h-4" />
            Pausa
          </button>
        )}
        {status === "paused" && (
          <button
            type="button"
            onClick={handleResume}
            data-testid="cronometro-resume-button"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500/90 text-black text-sm tracking-wide font-medium hover:bg-emerald-400 transition"
          >
            <Play className="w-4 h-4" />
            Riprendi
          </button>
        )}
        <button
          type="button"
          onClick={handleReset}
          disabled={status === "idle" && displayMs === 0}
          data-testid="cronometro-reset-button"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-transparent border border-white/15 text-white/70 text-sm tracking-wide hover:text-white hover:border-white/30 transition disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <Square className="w-4 h-4" />
          Azzera
        </button>
      </div>
    </div>
  );
}
